import React, { useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { SandboxHlsPlayer } from './SandboxHlsPlayer';
import { resolveSandboxMediaUrl } from '../../utils/sandboxCourseApi';
import type { IProject } from '../../types/global/helper';

export type SandboxPendingSubmission = {
    _id: string;
    notes?: string;
    class_title?: string;
    course?: { _id?: string; title?: string } | string;
    user?: { firstName?: string; lastName?: string; userName?: string; photo?: string };
    project?: Partial<IProject> | string | null;
};

type Props = {
    open: boolean;
    submission: SandboxPendingSubmission | null;
    busy?: boolean;
    onClose: () => void;
    onPass: (feedback: string) => void | Promise<void>;
    onRequestRevision: (feedback: string) => void | Promise<void>;
};

/**
 * Teacher review sheet: watch the student cut, leave feedback, pass or request revision.
 */
export function SandboxReviewSubmissionModal({ open, submission, busy, onClose, onPass, onRequestRevision }: Props) {
    const [feedback, setFeedback] = useState('');

    const project =
        submission?.project && typeof submission.project === 'object' ? (submission.project as IProject) : null;
    const canPlay = Boolean(project?._id && project.hls);
    const thumb = resolveSandboxMediaUrl(project?.feature_image || project?.thumbnail) || undefined;
    const student =
        submission?.user?.userName ||
        [submission?.user?.firstName, submission?.user?.lastName].filter(Boolean).join(' ') ||
        'Student';
    const courseTitle = typeof submission?.course === 'object' ? submission?.course?.title : 'Course';

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="md"
            TransitionProps={{
                onEnter: () => setFeedback(''),
            }}
            PaperProps={{
                sx: {
                    bgcolor: OBSIDIAN.surfaceContainerHigh,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    border: `1px solid ${OBSIDIAN.outline}44`,
                    overflow: 'hidden',
                },
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    px: 2.5,
                    py: 2,
                    borderBottom: `1px solid ${OBSIDIAN.outline}33`,
                }}>
                <Box sx={{ minWidth: 0, pr: 2 }}>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: 18,
                            color: OBSIDIAN.onSurface,
                        }}>
                        Review submission
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 13,
                            color: OBSIDIAN.onSurfaceVariant,
                            mt: 0.25,
                        }}>
                        {student} · {courseTitle}
                        {submission?.class_title ? ` · ${submission.class_title}` : ''}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={busy} size="small" sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {canPlay && project ? (
                    <SandboxHlsPlayer project={project} />
                ) : (
                    <Box
                        sx={{
                            aspectRatio: '16 / 9',
                            borderRadius: 2,
                            bgcolor: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundImage: thumb ? `url(${thumb})` : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}>
                        <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, px: 2, textAlign: 'center' }}>
                            {project?.processing_complete === false
                                ? 'This cut is still encoding. Wait a minute, close this, then open Watch & review again.'
                                : 'No playable video on this submission yet. Ask the student to re-upload if it stays stuck.'}
                        </Typography>
                    </Box>
                )}

                {project?.title ? (
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontWeight: 600,
                            fontSize: 14,
                            color: OBSIDIAN.onSurface,
                        }}>
                        {project.title}
                    </Typography>
                ) : null}

                {submission?.notes ? (
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: OBSIDIAN.surfaceContainer,
                            border: `1px solid ${OBSIDIAN.outline}33`,
                        }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: OBSIDIAN.onSurfaceVariant,
                                mb: 0.5,
                            }}>
                            Director notes
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: OBSIDIAN.onSurface,
                                fontFamily: OBSIDIAN_BODY_FONT,
                                whiteSpace: 'pre-wrap',
                            }}>
                            {submission.notes}
                        </Typography>
                    </Box>
                ) : null}

                <TextField
                    label="Your feedback to the student"
                    value={feedback}
                    onChange={e => setFeedback(e.target.value)}
                    multiline
                    minRows={3}
                    fullWidth
                    disabled={busy}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            bgcolor: '#323232',
                            color: OBSIDIAN.onSurface,
                            '& fieldset': { borderColor: `${OBSIDIAN.outline}55` },
                        },
                        '& label': { color: OBSIDIAN.onSurfaceVariant },
                    }}
                />

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                        disabled={busy}
                        onClick={() => void onRequestRevision(feedback.trim() || 'Please revise and resubmit')}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            color: '#ff7351',
                            border: '1px solid #ff735155',
                            borderRadius: 999,
                            px: 2,
                        }}>
                        {busy ? <CircularProgress size={18} sx={{ color: '#ff7351' }} /> : 'Needs revision'}
                    </Button>
                    <Button
                        disabled={busy}
                        onClick={() => void onPass(feedback.trim() || 'Approved')}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 999,
                            px: 2.5,
                            bgcolor: OBSIDIAN.primary,
                            color: OBSIDIAN.onPrimaryFixed,
                            '&:hover': { bgcolor: OBSIDIAN.primary, filter: 'brightness(1.05)' },
                        }}>
                        {busy ? <CircularProgress size={18} sx={{ color: OBSIDIAN.onPrimaryFixed }} /> : 'Pass'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
